// Static Pokémon → type(s) mapping used by the trainer card to render
// coloured type tags and the animated themed backdrop.
//
// The DB field `monster.pokemon_type` is often empty or malformed for legacy
// records, so we derive types from the Pokémon name (canonical Bulbapedia
// values). Names are matched case-insensitively.
//
// Types use the internal keys (lowercase, no accents) so the palette /
// icon lookup in `pokemonTypeVisuals` can key off them.

export const POKEMON_TYPES = {
  // Gen 1 starters
  bulbasaur: ['grass', 'poison'], ivysaur: ['grass', 'poison'], venusaur: ['grass', 'poison'],
  charmander: ['fire'], charmeleon: ['fire'], charizard: ['fire', 'flying'],
  squirtle: ['water'], wartortle: ['water'], blastoise: ['water'],
  // Gen 2 starters
  chikorita: ['grass'], bayleef: ['grass'], meganium: ['grass'],
  cyndaquil: ['fire'], quilava: ['fire'], typhlosion: ['fire'],
  totodile: ['water'], croconaw: ['water'], feraligatr: ['water'],
  // Gen 3
  treecko: ['grass'], grovyle: ['grass'], sceptile: ['grass'],
  torchic: ['fire'], combusken: ['fire', 'fighting'], blaziken: ['fire', 'fighting'],
  mudkip: ['water'], marshtomp: ['water', 'ground'], swampert: ['water', 'ground'],
  // Gen 4
  turtwig: ['grass'], grotle: ['grass'], torterra: ['grass', 'ground'],
  chimchar: ['fire'], monferno: ['fire', 'fighting'], infernape: ['fire', 'fighting'],
  piplup: ['water'], prinplup: ['water'], empoleon: ['water', 'steel'],
  // Gen 5
  snivy: ['grass'], servine: ['grass'], serperior: ['grass'],
  tepig: ['fire'], pignite: ['fire', 'fighting'], emboar: ['fire', 'fighting'],
  oshawott: ['water'], dewott: ['water'], samurott: ['water'],
  // Gen 6
  chespin: ['grass'], quilladin: ['grass'], chesnaught: ['grass', 'fighting'],
  fennekin: ['fire'], braixen: ['fire'], delphox: ['fire', 'psychic'],
  froakie: ['water'], frogadier: ['water'], greninja: ['water', 'dark'],
  // Gen 7
  rowlet: ['grass', 'flying'], dartrix: ['grass', 'flying'], decidueye: ['grass', 'ghost'],
  litten: ['fire'], torracat: ['fire'], incineroar: ['fire', 'dark'],
  popplio: ['water'], brionne: ['water'], primarina: ['water', 'fairy'],
  // Gen 8
  grookey: ['grass'], thwackey: ['grass'], rillaboom: ['grass'],
  scorbunny: ['fire'], raboot: ['fire'], cinderace: ['fire'],
  sobble: ['water'], drizzile: ['water'], inteleon: ['water'],
  // Gen 9
  sprigatito: ['grass'], floragato: ['grass'], meowscarada: ['grass', 'dark'],
  fuecoco: ['fire'], crocalor: ['fire'], skeledirge: ['fire', 'ghost'],
  quaxly: ['water'], quaxwell: ['water'], quaquaval: ['water', 'fighting'],

  // Iconic species
  pikachu: ['electric'], raichu: ['electric'],
  eevee: ['normal'], vaporeon: ['water'], jolteon: ['electric'], flareon: ['fire'],
  espeon: ['psychic'], umbreon: ['dark'], leafeon: ['grass'], glaceon: ['ice'], sylveon: ['fairy'],
  snorlax: ['normal'], mewtwo: ['psychic'], mew: ['psychic'],
  gengar: ['ghost', 'poison'], gastly: ['ghost', 'poison'], haunter: ['ghost', 'poison'],
  lucario: ['fighting', 'steel'], garchomp: ['dragon', 'ground'], dragonite: ['dragon', 'flying'],
  gyarados: ['water', 'flying'], magikarp: ['water'],
  jigglypuff: ['normal', 'fairy'], wigglytuff: ['normal', 'fairy'],
  meowth: ['normal'], persian: ['normal'],
  psyduck: ['water'], golduck: ['water'],
  machop: ['fighting'], machoke: ['fighting'], machamp: ['fighting'],
  abra: ['psychic'], kadabra: ['psychic'], alakazam: ['psychic'],
  onix: ['rock', 'ground'], steelix: ['steel', 'ground'],
  scyther: ['bug', 'flying'], scizor: ['bug', 'steel'],
  lapras: ['water', 'ice'], articuno: ['ice', 'flying'], zapdos: ['electric', 'flying'], moltres: ['fire', 'flying'],
  suicune: ['water'], entei: ['fire'], raikou: ['electric'],
  rayquaza: ['dragon', 'flying'], groudon: ['ground'], kyogre: ['water'],
  dialga: ['steel', 'dragon'], palkia: ['water', 'dragon'], giratina: ['ghost', 'dragon'],
  arceus: ['normal'], darkrai: ['dark'],
  lugia: ['psychic', 'flying'], hooh: ['fire', 'flying'],
  celebi: ['psychic', 'grass'], jirachi: ['steel', 'psychic'], deoxys: ['psychic'],

  // Common non-starters that show up frequently
  pidgey: ['normal', 'flying'], pidgeotto: ['normal', 'flying'], pidgeot: ['normal', 'flying'],
  rattata: ['normal'], raticate: ['normal'],
  spearow: ['normal', 'flying'], fearow: ['normal', 'flying'],
  ekans: ['poison'], arbok: ['poison'],
  sandshrew: ['ground'], sandslash: ['ground'],
  nidoranf: ['poison'], nidorina: ['poison'], nidoqueen: ['poison', 'ground'],
  nidoranm: ['poison'], nidorino: ['poison'], nidoking: ['poison', 'ground'],
  clefairy: ['fairy'], clefable: ['fairy'],
  vulpix: ['fire'], ninetales: ['fire'],
  zubat: ['poison', 'flying'], golbat: ['poison', 'flying'], crobat: ['poison', 'flying'],
  oddish: ['grass', 'poison'], gloom: ['grass', 'poison'], vileplume: ['grass', 'poison'],
  paras: ['bug', 'grass'], parasect: ['bug', 'grass'],
  venonat: ['bug', 'poison'], venomoth: ['bug', 'poison'],
  diglett: ['ground'], dugtrio: ['ground'],
  growlithe: ['fire'], arcanine: ['fire'],
  poliwag: ['water'], poliwhirl: ['water'], poliwrath: ['water', 'fighting'],
  bellsprout: ['grass', 'poison'], weepinbell: ['grass', 'poison'], victreebel: ['grass', 'poison'],
  tentacool: ['water', 'poison'], tentacruel: ['water', 'poison'],
  geodude: ['rock', 'ground'], graveler: ['rock', 'ground'], golem: ['rock', 'ground'],
  ponyta: ['fire'], rapidash: ['fire'],
  slowpoke: ['water', 'psychic'], slowbro: ['water', 'psychic'],
  magnemite: ['electric', 'steel'], magneton: ['electric', 'steel'],
  farfetchd: ['normal', 'flying'],
  doduo: ['normal', 'flying'], dodrio: ['normal', 'flying'],
  seel: ['water'], dewgong: ['water', 'ice'],
  grimer: ['poison'], muk: ['poison'],
  shellder: ['water'], cloyster: ['water', 'ice'],
  drowzee: ['psychic'], hypno: ['psychic'],
  krabby: ['water'], kingler: ['water'],
  voltorb: ['electric'], electrode: ['electric'],
  exeggcute: ['grass', 'psychic'], exeggutor: ['grass', 'psychic'],
  cubone: ['ground'], marowak: ['ground'],
  hitmonlee: ['fighting'], hitmonchan: ['fighting'],
  lickitung: ['normal'],
  koffing: ['poison'], weezing: ['poison'],
  rhyhorn: ['ground', 'rock'], rhydon: ['ground', 'rock'],
  chansey: ['normal'],
  tangela: ['grass'],
  kangaskhan: ['normal'],
  horsea: ['water'], seadra: ['water'],
  goldeen: ['water'], seaking: ['water'],
  staryu: ['water'], starmie: ['water', 'psychic'],
  mrmime: ['psychic', 'fairy'],
  jynx: ['ice', 'psychic'],
  electabuzz: ['electric'],
  magmar: ['fire'],
  pinsir: ['bug'],
  tauros: ['normal'],
  ditto: ['normal'],
  porygon: ['normal'],
  omanyte: ['rock', 'water'], omastar: ['rock', 'water'],
  kabuto: ['rock', 'water'], kabutops: ['rock', 'water'],
  aerodactyl: ['rock', 'flying'],
};

// Type visual metadata: colour + icon + subtle background motif.
export const TYPE_VISUALS = {
  normal:   { label: 'Normal',    color: '#A8A77A', textColor: '#3d3d2b' },
  fire:     { label: 'Fogo',      color: '#EE8130', textColor: '#fff' },
  water:    { label: 'Água',      color: '#6390F0', textColor: '#fff' },
  electric: { label: 'Elétrico',  color: '#F7D02C', textColor: '#4a3d00' },
  grass:    { label: 'Planta',    color: '#7AC74C', textColor: '#fff' },
  ice:      { label: 'Gelo',      color: '#96D9D6', textColor: '#0a4247' },
  fighting: { label: 'Lutador',   color: '#C22E28', textColor: '#fff' },
  poison:   { label: 'Veneno',    color: '#A33EA1', textColor: '#fff' },
  ground:   { label: 'Terra',     color: '#E2BF65', textColor: '#4a3400' },
  flying:   { label: 'Voador',    color: '#A98FF3', textColor: '#fff' },
  psychic:  { label: 'Psíquico',  color: '#F95587', textColor: '#fff' },
  bug:      { label: 'Inseto',    color: '#A6B91A', textColor: '#fff' },
  rock:     { label: 'Pedra',     color: '#B6A136', textColor: '#fff' },
  ghost:    { label: 'Fantasma',  color: '#735797', textColor: '#fff' },
  dragon:   { label: 'Dragão',    color: '#6F35FC', textColor: '#fff' },
  dark:     { label: 'Sombrio',   color: '#705746', textColor: '#fff' },
  steel:    { label: 'Aço',       color: '#B7B7CE', textColor: '#2b2b3d' },
  fairy:    { label: 'Fada',      color: '#D685AD', textColor: '#fff' },
};

// Normalize a Pokémon name to the mapping key.
const _norm = (n) =>
  (n || '')
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

/**
 * Return an array of 0-2 type keys for a monster.
 *  1) prefer explicit `monster.pokemon_type` (accept string or array)
 *  2) fall back to the static mapping by canonical Pokémon name
 *  3) empty array if unknown
 */
export const getPokemonTypes = (monster) => {
  if (!monster) return [];
  // Try explicit field first (accept 'fire', 'fire/flying', 'Fire, Flying', ['fire','flying'])
  const raw = monster.pokemon_type;
  if (Array.isArray(raw)) {
    const keys = raw.map(_norm).filter((k) => TYPE_VISUALS[k]);
    if (keys.length) return keys.slice(0, 2);
  } else if (typeof raw === 'string' && raw.trim()) {
    const keys = raw
      .split(/[\/,|;\s]+/)
      .map(_norm)
      .filter((k) => TYPE_VISUALS[k]);
    if (keys.length) return keys.slice(0, 2);
  }
  // Fall back to static mapping by base species name
  const name = _norm(monster.species || monster.base_name || monster.name);
  return POKEMON_TYPES[name] || [];
};
